import { useState, useEffect, useMemo } from "react";
import { Loader2, ShieldCheck, X, RotateCcw, Save, Info, History, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * PermissionsEditor
 * -----------------
 * Modal, in dem der Direktor/Admin für einen einzelnen User die einzelnen
 * Capabilities per Häkchen aktivieren oder entziehen kann.
 *
 * - Die Rollen-Defaults werden als „Standard" angezeigt (grau gecheckt)
 * - Häkchen setzen = zusätzliche Erlaubnis (grant)
 * - Rollen-Default abhaken = revoke
 * - Speichern schickt `{grant, revoke}` an PATCH /api/users/{id}/permissions
 */
export default function PermissionsEditor({ userId, userName, userRole, onClose }) {
  const [catalog, setCatalog] = useState(null); // {capabilities: {cap: label}, role_defaults: {role: [caps]}}
  const [current, setCurrent] = useState(null); // {override: {grant, revoke}, effective: [...]}
  const [selected, setSelected] = useState(new Set()); // in "on"-Zustand nach User-Toggle
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c1, c2] = await Promise.all([
          api.get("/capabilities"),
          api.get(`/users/${userId}/permissions`),
        ]);
        setCatalog(c1.data);
        setCurrent(c2.data);
        setSelected(new Set(c2.data.effective));
      } catch {
        toast.error("Konnte Rechte nicht laden");
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  const roleDefaults = useMemo(
    () => new Set(catalog?.role_defaults?.[userRole] || []),
    [catalog, userRole]
  );

  const toggle = (cap) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(cap)) n.delete(cap);
      else n.add(cap);
      return n;
    });
  };

  const resetToDefaults = () => {
    setSelected(new Set(roleDefaults));
  };

  const save = async () => {
    // Aus dem finalen Set die Grants (extra) und Revokes (Rollen-Default weg) ableiten
    const grant = [...selected].filter((c) => !roleDefaults.has(c));
    const revoke = [...roleDefaults].filter((c) => !selected.has(c));
    setSaving(true);
    try {
      await api.patch(`/users/${userId}/permissions`, { grant, revoke });
      toast.success("Rechte gespeichert");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const cats = catalog?.capabilities || {};

  // Iter88w (2026-02-19): 3-Klick-Presets für typische Rollen-Bündel.
  // Presets nehmen die Rollen-Defaults als Grundlage und wenden zusätzlich
  // eigene grants/revokes an — sichtbar über selected-Set.
  const PRESETS = useMemo(() => ({
    telesales_innendienst: {
      label: "Telesales Innendienst (Nur Erfassung + Orientierungsangebot)",
      description: "Kunde anlegen, Verbrauch/Adresse erfassen, Orientierungsangebot für WP + PV erstellen und mailen. Keine Katalog- oder Team-Rechte.",
      caps: ["create_project", "edit_own_project", "create_orientation_offer"],
    },
    solo_vertrieb: {
      label: "Nur eigene Kunden (Solo-Vertrieb)",
      description: "Volle Angebots-Rechte, aber NUR auf selbst erstellte Projekte. Keine Team-Übersicht, keine fremden Kunden.",
      caps: ["create_project", "edit_own_project", "create_orientation_offer", "convert_orientation_offer", "edit_offer", "send_offer_pdf"],
    },
    aussendienst: {
      label: "Außendienst (Angebote finalisieren + eigene Projekte)",
      description: "Wie Innendienst plus: Orientierungsangebot in finales Angebot umwandeln, Angebote editieren, PDF-Versand.",
      caps: ["create_project", "edit_own_project", "create_orientation_offer", "convert_orientation_offer", "edit_offer", "send_offer_pdf"],
    },
    vertriebsleiter: {
      label: "Vertriebsleiter (Team-Übersicht + Übergabe)",
      description: "Außendienst-Rechte plus: alle Team-Projekte sehen, Marge übersteuern, Team-Analytics, Übergabe Innendienst → Außendienst.",
      caps: ["create_project", "edit_own_project", "see_all_projects", "create_orientation_offer", "convert_orientation_offer", "edit_offer", "send_offer_pdf", "override_margin_per_offer", "see_team_analytics", "handover_intake_project", "see_intake_pipeline", "give_discount_above_cap"],
    },
    read_only_mgmt: {
      label: "Read-Only Management (nur Einsicht, keine Änderungen)",
      description: "Alle Projekte + Team-Analytics sehen — aber keine Bearbeitung, kein Katalog, keine Nutzerverwaltung.",
      caps: ["see_all_projects", "see_team_analytics", "see_intake_pipeline"],
    },
  }), []);

  const applyPreset = (key) => {
    const p = PRESETS[key];
    if (!p) return;
    // Whitelist auf tatsächlich existierende Caps.
    const wanted = new Set(p.caps.filter((c) => c in (catalog?.capabilities || {})));
    setSelected(wanted);
    toast.success(`Preset „${p.label.split(" (")[0]}" übernommen — Speichern nicht vergessen.`);
  };
  const groups = useMemo(() => {
    const g = {
      "Projekte & Kunden": ["create_project", "edit_own_project", "see_all_projects", "delete_project"],
      "Angebote": ["create_orientation_offer", "convert_orientation_offer", "edit_offer", "delete_offer", "send_offer_pdf", "override_margin_per_offer", "give_discount_above_cap"],
      "Katalog & Pakete": ["manage_wp_catalog", "manage_pv_catalog", "see_catalog_raw_prices"],
      "Team & Nutzer": ["manage_team", "manage_users", "manage_user_permissions", "see_team_analytics", "handover_intake_project", "see_intake_pipeline"],
      "Konfiguration": ["manage_provision_and_discount", "manage_tenant_settings", "manage_promo_codes", "manage_routing_rules"],
      "System (nur Super-Admin)": ["manage_tenants", "manage_api_keys", "manage_system_health", "manage_compliance"],
    };
    return g;
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="permissions-editor-modal">
      <div className="bg-[#18181B] border border-emerald-500/30 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="border-b border-zinc-800 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-[#00E575]" />
            <h3 className="font-display text-lg font-semibold text-[#00E575]">Rechte bearbeiten</h3>
            <span className="text-[12px] text-zinc-300 font-medium">· {userName} · {userRole}</span>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100" data-testid="permissions-close">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-zinc-400 gap-2">
              <Loader2 className="animate-spin" size={14} /> Lade Rechte…
            </div>
          ) : (
            <>
              <div className="mb-4 flex items-start gap-2 rounded-xl bg-[#0F1E18] border border-[#00E575]/30 px-3 py-2.5">
                <Info size={14} className="text-[#00E575] mt-0.5 shrink-0" />
                <div className="text-[12px] text-zinc-300 leading-snug font-medium">
                  Rollen-Standards sind mit grünem Rand markiert. Häkchen setzen = Recht zusätzlich gewähren. Standard-Häkchen entfernen = Recht entziehen.
                </div>
              </div>

              {/* Iter88w: Preset-Dropdown für 3-Klick-Rechtevergabe */}
              <div className="mb-4 rounded-xl bg-zinc-900/60 border border-zinc-800 p-3">
                <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-bold mb-2">
                  🚀 Preset — Alle Rechte auf einmal setzen
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {Object.entries(PRESETS).map(([key, p]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => applyPreset(key)}
                      data-testid={`permission-preset-${key}`}
                      className="text-left rounded-lg bg-[#0F0F11] border border-zinc-800 hover:border-emerald-500/50 hover:bg-emerald-500/5 px-3 py-2 transition"
                    >
                      <div className="text-[12.5px] font-semibold text-zinc-100 leading-tight">{p.label}</div>
                      <div className="text-[11px] text-zinc-400 mt-1 leading-snug">{p.description}</div>
                    </button>
                  ))}
                </div>
                <p className="text-[10.5px] text-zinc-500 mt-2 italic">
                  Presets ersetzen die aktuelle Auswahl. Danach kannst du einzelne Rechte manuell anpassen und speichern.
                </p>
              </div>

              {Object.entries(groups).map(([groupName, caps]) => {
                const groupCaps = caps.filter((c) => c in cats);
                if (groupCaps.length === 0) return null;
                return (
                  <div key={groupName} className="mb-4">
                    <div className="text-[12px] uppercase tracking-wider font-semibold text-zinc-200 mb-2">{groupName}</div>
                    <div className="space-y-1.5">
                      {groupCaps.map((cap) => {
                        const isDefault = roleDefaults.has(cap);
                        const isChecked = selected.has(cap);
                        return (
                          <label
                            key={cap}
                            data-testid={`permission-row-${cap}`}
                            className={`flex items-start gap-2.5 rounded-lg px-2.5 py-2 cursor-pointer transition ${
                              isChecked
                                ? isDefault ? "bg-[#00E575]/10 border border-[#00E575]/40" : "bg-amber-500/10 border border-amber-500/40"
                                : "bg-[#0F0F11] border border-zinc-800 hover:border-zinc-700"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => toggle(cap)}
                              className="mt-0.5 accent-[#00E575]"
                              data-testid={`permission-checkbox-${cap}`}
                            />
                            <div className="flex-1">
                              <div className="text-[13px] text-zinc-100 font-medium">{cats[cap]}</div>
                              <div className="text-[12px] text-zinc-400 font-mono">{cap}</div>
                            </div>
                            {isDefault && (
                              <span className="text-[12px] text-[#00E575] font-semibold uppercase tracking-wider self-center">Standard</span>
                            )}
                            {isChecked && !isDefault && (
                              <span className="text-[12px] text-amber-300 font-semibold uppercase tracking-wider self-center">Zusatz</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {/* Iter88x — DSGVO-Historie */}
              <AuditHistoryPanel userId={userId} />
            </>
          )}
        </div>

        <div className="border-t border-zinc-800 px-5 py-3 flex justify-between gap-2">
          <button
            onClick={resetToDefaults}
            disabled={loading || saving}
            data-testid="permissions-reset"
            className="h-9 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-[12px] font-medium text-zinc-200 inline-flex items-center gap-1.5 disabled:opacity-40"
          >
            <RotateCcw size={12} /> Rollen-Standard
          </button>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              disabled={saving}
              className="h-9 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-[12px] font-medium text-zinc-200 disabled:opacity-40"
            >
              Abbrechen
            </button>
            <button
              onClick={save}
              disabled={loading || saving}
              data-testid="permissions-save"
              className="h-9 px-4 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12px] inline-flex items-center gap-1.5 hover:bg-[#00C964] disabled:opacity-40"
            >
              {saving ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />}
              Speichern
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


/**
 * AuditHistoryPanel — Iter88x
 * ─────────────────────────────
 * Collapsible-Panel unter den Capability-Checkboxen. Zeigt die letzten 20
 * Rechte-Änderungen für den aktuellen User (aus `permission_audit_log`).
 * DSGVO-relevant: Nachvollziehbar wer, wann, was geändert hat.
 */
function AuditHistoryPanel({ userId }) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reverting, setReverting] = useState(null); // entry_id being reverted

  const load = () => {
    setLoading(true);
    api.get(`/admin/permission-audit?user_id=${userId}&limit=20`)
      .then((r) => setEntries(r.data?.entries || []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open || entries !== null) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId]);

  const revert = async (entryId, actorName) => {
    if (!window.confirm(`Wirklich auf den Stand VOR dieser Änderung zurücksetzen? (Ursprünglich geändert von ${actorName || "?"})`)) return;
    setReverting(entryId);
    try {
      await api.post(`/admin/permission-audit/${entryId}/revert`);
      toast.success("Rechte zurückgesetzt");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Zurücksetzen fehlgeschlagen");
    } finally {
      setReverting(null);
    }
  };

  return (
    <div className="mt-6 rounded-xl bg-zinc-900/50 border border-zinc-800 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="permission-audit-toggle"
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-zinc-900 transition"
      >
        <History size={14} className="text-amber-300" />
        <span className="text-[13px] font-semibold text-zinc-100 flex-1">Rechte-Änderungs-Historie (DSGVO)</span>
        {open ? <ChevronUp size={14} className="text-zinc-400" /> : <ChevronDown size={14} className="text-zinc-400" />}
      </button>
      {open && (
        <div className="border-t border-zinc-800 px-4 py-3">
          {loading ? (
            <div className="flex items-center gap-2 text-[12px] text-zinc-400">
              <Loader2 size={12} className="animate-spin" /> Lade …
            </div>
          ) : (entries || []).length === 0 ? (
            <div className="text-[12px] text-zinc-500 italic">Noch keine Rechte-Änderungen protokolliert.</div>
          ) : (
            <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="permission-audit-list">
              {entries.map((e) => (
                <div key={e.id} className="text-[12px] rounded-lg bg-[#0F0F11] border border-zinc-800 px-3 py-2">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="font-semibold text-zinc-100">
                      {e.actor_name || "?"} <span className="text-zinc-500 font-normal">· {e.actor_role || "—"}</span>
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-zinc-500 font-mono text-[11px]">
                        {new Date(e.changed_at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}
                      </span>
                      {/* Iter88y: Revert nur bei "echten" Änderungen (nicht bei bereits reverten Einträgen) */}
                      {e.action !== "permissions_reverted" && ((e.added_caps || []).length > 0 || (e.removed_caps || []).length > 0) && (
                        <button
                          type="button"
                          data-testid={`permission-audit-revert-${e.id}`}
                          onClick={() => revert(e.id, e.actor_name)}
                          disabled={reverting === e.id}
                          title="Auf diesen Stand zurücksetzen"
                          className="h-6 px-2 rounded text-[10.5px] font-bold uppercase tracking-wider bg-rose-500/15 border border-rose-500/40 text-rose-200 hover:bg-rose-500/25 inline-flex items-center gap-1 disabled:opacity-50"
                        >
                          {reverting === e.id ? <Loader2 size={9} className="animate-spin"/> : <RotateCcw size={9}/>}
                          Zurücksetzen
                        </button>
                      )}
                    </div>
                  </div>
                  {e.action === "permissions_bulk_applied" && (
                    <div className="text-[11px] text-amber-300 uppercase tracking-wider font-bold mb-1">Bulk-Vergabe</div>
                  )}
                  {e.action === "permissions_reverted" && (
                    <div className="text-[11px] text-rose-300 uppercase tracking-wider font-bold mb-1">↺ Zurücksetzen</div>
                  )}
                  {(e.added_caps || []).length > 0 && (
                    <div className="text-[11.5px] text-emerald-300 leading-snug">
                      <span className="font-semibold">+ Hinzugefügt:</span> {e.added_caps.join(", ")}
                    </div>
                  )}
                  {(e.removed_caps || []).length > 0 && (
                    <div className="text-[11.5px] text-rose-300 leading-snug">
                      <span className="font-semibold">− Entzogen:</span> {e.removed_caps.join(", ")}
                    </div>
                  )}
                  {(e.added_caps || []).length === 0 && (e.removed_caps || []).length === 0 && (
                    <div className="text-[11.5px] text-zinc-500 italic">Keine Änderung am effektiven Rechteset.</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
